/**
 * Context Menu.
 *
 * A menu of functions to perform on a Thing.
 *
 * TODO: Re-factor this as two separate dialogs.
 *       https://github.com/WebThingsIO/gateway/issues/3098
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/.
 */
'use strict';

const API = require('./api').default;
const App = require('./app');
const Icons = require('./icons');
const page = require('page');
const Utils = require('./utils');
const fluent = require('./fluent');

const ContextMenu = {
  /**
   * Initialise Add Thing Screen.
   */
  init: function () {
    this.element = document.getElementById('context-menu');
    this.editContent = document.getElementById('context-menu-content-edit');
    this.removeContent = document.getElementById('context-menu-content-remove');
    this.backButton = document.getElementById('context-menu-back-button');
    this.headingIcon = document.getElementById('context-menu-heading-icon');
    this.headingCustomIcon = document.getElementById('context-menu-heading-custom-icon');
    this.headingText = document.getElementById('context-menu-heading-text');
    this.saveButton = document.getElementById('edit-thing-save-button');
    this.thingIcon = document.getElementById('edit-thing-icon');
    this.titleInput = document.getElementById('edit-thing-title');
    this.thingType = document.getElementById('edit-thing-type');
    this.customIcon = document.getElementById('edit-thing-custom-icon');
    this.customIconInput = document.getElementById('edit-thing-custom-icon-input');
    this.customIconLabel = document.getElementById('edit-thing-custom-icon-label');
    this.showOnFloorplanCheckbox = document.getElementById('show-on-floorplan-checkbox');
    this.label = document.getElementById('edit-thing-label');
    this.removeButton = document.getElementById('remove-thing-button');
    this.logoutForm = document.getElementById('logout');
    this.thingId = '';

    // Add event listeners
    window.addEventListener('_contextmenu', this.show.bind(this));
    this.backButton.addEventListener('click', this.hide.bind(this));
    this.saveButton.addEventListener('click', this.handleEdit.bind(this));
    this.removeButton.addEventListener('click', this.handleRemove.bind(this));
    this.logoutForm.addEventListener('submit', (e) => {
      e.preventDefault();
      API.logout().then(() => {
        window.location.href = '/login';
      });
    });
    this.thingType.addEventListener('change', this.handleTypeChange.bind(this));
    this.customIconInput.addEventListener('change', this.handleIconUpload.bind(this));
  },

  /**
   * Show Context Menu.
   */
  show: function (e) {
    this.iconData = null;
    this.customIcon.iconHref = '';

    if (e.detail.iconHref) {
      this.headingCustomIcon.classList.remove('hidden');
      this.headingCustomIcon.iconHref = e.detail.iconHref;
      this.headingIcon.classList.add('custom-thing');
      this.headingIcon.style.backgroundImage = '';
      this.customIcon.iconHref = e.detail.iconHref;
    } else {
      this.headingCustomIcon.classList.add('hidden');
      this.headingIcon.classList.remove('custom-thing');
      this.headingIcon.style.backgroundImage = `url("${e.detail.thingIcon}")`;
      this.customIcon.iconHref = '';
    }

    this.headingText.textContent = e.detail.thingTitle;
    this.thingId = e.detail.thingId;
    this.element.classList.remove('hidden');

    this.editContent.classList.add('hidden');
    this.removeContent.classList.add('hidden');

    switch (e.detail.action) {
      case 'edit': {
        this.thingType.disabled = false;
        this.titleInput.disabled = false;
        this.saveButton.disabled = false;
        this.customIconInput.disabled = false;
        this.titleInput.value = e.detail.thingTitle;
        this.showOnFloorplanCheckbox.checked = e.detail.floorplanVisibility;
        this.thingType.innerHTML = '';

        if (!e.detail.selectedCapability || e.detail.selectedCapability === 'Custom') {
          this.thingIcon.classList.add('custom-thing');
          this.thingIcon.style.backgroundImage = '';
          this.customIconLabel.classList.remove('hidden');
          this.customIcon.classList.remove('hidden');
        } else {
          this.thingIcon.classList.remove('custom-thing');
          this.thingIcon.style.backgroundImage = `url("${e.detail.thingIcon}")`;
          this.customIconLabel.classList.add('hidden');
          this.customIcon.classList.add('hidden');
        }

        const capabilities = Utils.sortCapabilities(e.detail.capabilities);
        capabilities.push('Custom');

        for (const capability of capabilities) {
          const option = document.createElement('option');
          option.value = capability;

          if (
            e.detail.selectedCapability === capability ||
            (capability === 'Custom' && !e.detail.selectedCapability)
          ) {
            option.selected = true;
          }

          option.innerText = fluent.getMessageStrict(capability) || capability;

          this.thingType.appendChild(option);
        }

        this.editContent.classList.remove('hidden');
        break;
      }
      case 'remove':
        this.removeContent.classList.remove('hidden');
        break;
    }
  },

  /**
   * Hide Context Menu.
   */
  hide: function () {
    this.element.classList.add('hidden');
    this.headingIcon.classList.remove('hidden');
    this.headingCustomIcon.classList.add('hidden');
    this.headingText.textContent = '';
    this.thingId = '';
  },

  handleTypeChange: function () {
    const capability = this.thingType.options[this.thingType.selectedIndex].value;

    this.customIconLabel.classList.add('hidden');
    this.customIcon.classList.add('hidden');

    let image = '';
    if (capability === 'Custom') {
      this.customIconLabel.classList.remove('hidden');
      this.customIcon.classList.remove('hidden');
    } else if (Icons.capabilityHasIcon(capability)) {
      image = Icons.capabilityToIcon(capability);
    }

    if (image) {
      this.thingIcon.classList.remove('custom-thing');
      this.thingIcon.style.backgroundImage = `url("${image}")`;
    } else {
      this.thingIcon.classList.add('custom-thing');
      this.thingIcon.style.backgroundImage = '';
    }
  },

  handleIconUpload: function () {
    this.label.classList.add('hidden');

    if (this.customIconInput.files.length === 0) {
      return;
    }

    const file = this.customIconInput.files[0];
    if (!['image/jpeg', 'image/png', 'image/svg+xml'].includes(file.type)) {
      this.label.innerText = fluent.getMessage('invalid-file');
      this.label.classList.add('error');
      this.label.classList.remove('hidden');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (e.target.error) {
        console.error(e.target.error);
        this.label.innerText = fluent.getMessage('failed-read-file');
        this.label.classList.add('error');
        this.label.classList.remove('hidden');
        this.saveButton.disabled = false;
        return;
      }

      this.iconData = {
        mime: file.type,
        data: btoa(e.target.result),
      };
      this.saveButton.disabled = false;

      this.customIcon.iconHref = URL.createObjectURL(file);
      this.customIcon.classList.remove('hidden');
    };

    this.saveButton.disabled = true;
    reader.readAsBinaryString(file);
  },

  /**
   * Handle click on edit option.
   */
  handleEdit: function () {
    this.thingType.disabled = true;
    this.titleInput.disabled = true;
    this.saveButton.disabled = true;
    this.customIconInput.disabled = true;

    const title = this.titleInput.value.trim();
    if (title.length === 0) {
      return;
    }

    let capability;
    if (this.thingType.options.length > 0) {
      capability = this.thingType.options[this.thingType.selectedIndex].value;
    }

    const floorplanVisibility = this.showOnFloorplanCheckbox.checked;

    const body = { title, floorplanVisibility, selectedCapability: capability };

    if (capability === 'Custom' && this.iconData) {
      body.iconData = this.iconData;
    }
    App.gatewayModel
      .updateThing(this.thingId, body)
      .then(() => {
        this.hide();
        this.saveButton.disabled = false;
      })
      .catch((error) => {
        console.error(`Error updating thing: ${error}`);
        this.label.innerText = fluent.getMessage('failed-save');
        this.label.classList.add('error');
        this.label.classList.remove('hidden');
        this.thingType.disabled = false;
        this.titleInput.disabled = false;
        this.saveButton.disabled = false;
        this.customIconInput.disabled = false;
      });
  },

  /**
   * Handle click on remove option.
   */
  handleRemove: function () {
    App.gatewayModel
      .removeThing(this.thingId)
      .then(() => {
        page('/things');
        this.hide();
      })
      .catch((error) => {
        console.error(`Error removing thing: ${error}`);
        this.hide();
      });
  },
};

module.exports = ContextMenu;
